import getValidSubmissions from '@/containers/E4ImpactMBA/helpers/getValidSubmissions';
import {COLORS} from '@/utils/constants';

export const transformData = [
    {
        name: 'RISK-TAKING',
        key: 'risk',
    },
    {
        name: 'INNOVATIVENESS',
        key: 'inn',
    },
    {
        name: 'PROACTIVENESS',
        key: 'pro',
    },
    {
        name: 'COMPETITIVE AGGRESSIVENESS',
        key: 'comp',
    },
    {
        name: 'AUTONOMY',
        key: 'aut',
    },
];

export const baselineStructure = {
    entr_orient: {
        t0: {
            risk: ['a', 'b', 'c'],
            inn: ['d', 'e', 'f'],
            pro: ['g', 'h', 'i'],
            comp: ['l', 'm', 'n'],
            aut: ['o', 'p', 'q', 'r', 's', 't']
        }
    }
}

export const finalStructure = {
    entr_orient: {
        t1: {
            risk: ['a', 'b', 'c'],
            inn: ['d', 'e', 'f'],
            proact: ['g', 'h', 'i'],
            compet: ['l', 'm', 'n'],
            aut: ['o', 'p', 'q', 'r', 's', 't']
        }
    },
    matrixAliases: {
        pro: 'proact',
        comp: 'compet'
    }
}

function generateKeys(structure: any, specificMatrix: string) {
    const keys: string[] = [];
  
    const entrOrient = structure.entr_orient;

    for (const time in entrOrient) {
        const matrices = entrOrient[time];
        const specificMatrixKey = structure?.matrixAliases?.[specificMatrix] || specificMatrix;

        matrices[specificMatrixKey].forEach((row: any) => {
            keys.push(`entr_orient_${specificMatrixKey}_${time}_entr_orient_${row}_${time}`);
        });
    }
  
    return keys;
}

export const calculateTotalByKeyAndStructure = (submissions: any[], structure: any, key: string): number => {
    const total = submissions.reduce((acc: number, submission: any) => {
        const keys = generateKeys(structure, key);

        return acc + (keys.reduce((acc, key) => acc + Number(submission[key]), 0) / keys.length);
    }, 0);

    return total / submissions.length;
}

/**
 * question: On a scale of 1 (very low) to 7 (very high), how much are these statements relevant for you?
 * == baseline
 * entr_orient; index - t0
 *  - matrix - risk
 *      rows - a, b, c; columns - (1;7)
 *  - matrix - inn
 *      rows - d, e, f; columns - (1;7)
 *  - matrix - pro
 *      rows - g, h, i; columns - (1;7)
 *  - matrix - comp
 *      rows - l, m, n; columns - (1;7)
 *   - matrix - aut
 *      rows - o, p, q, r, s, t; columns - (1;7)
 * 
 * == final
 * entr_orient; index - t1
 *  - matrix - risk
 *      rows - a, b, c; columns - (1;7)
 *  - matrix - inn
 *      rows - d, e, f; columns - (1;7)
 *  - matrix - proact
 *      rows - g, h, i; columns - (1;7)
 *  - matrix - compet
 *      rows - l, m, n; columns - (1;7)
 *  - matrix - aut
 *      rows - o, p, q, r, s, t; columns - (1;7)
 * 
 * @param props 
 * @returns 
 */
export const entrepreneurMindsetChartCalculation = (props: any) => {
    const {filteredBaseline, filteredFinal} = getValidSubmissions(
        props.baselineSubmissions,
        props.finalSubmissions
    );
    
    const baselineData = transformData.map(
        ({name, key}) => ({
            name,
            y: +calculateTotalByKeyAndStructure(filteredBaseline, baselineStructure, key).toFixed(2)
        })
    );
    const finalData = transformData.map(
        ({name, key}) => ({
            name,
            y: +calculateTotalByKeyAndStructure(filteredFinal, finalStructure, key).toFixed(2)
        })
    );

    return {
        chart: {
            polar: true,
            style: {
                fontFamily: 'Rubik, sans-serif',
            },
            backgroundColor: '#fafafa',
        },
        title: {
            text: 'Entrepreneurs increase their entrepreneurial mindset and orientation',
            align: 'left',
        },
        xAxis: {
            categories: transformData.map(({name}) => name),
        },
        yAxis: {
            min: 0,
        },
        legend: {
            verticalAlign: 'top',
        },
        series: [
            {
                type: 'line',
                name: 'BASELINE',
                data: baselineData,
                color: COLORS.blue,
            },
            {
                type: 'line',
                name: 'FINAL',
                data: finalData,
                color: COLORS.yellow,
            },
        ],
    } as Highcharts.Options;
}